<template>
  <div class="container">
    <div
      v-if="!uploaded"
      v-cloak
      class="drop-zone"
      @drop.prevent="dropFile($event)"
      @dragover.prevent
      @click="$refs.uploadFile.click()"
    >
      <input id="qqz" ref="uploadFile" type="file" style="display: none;" @change="selectFile($event)" />
      <div class="aligned">
        <img class="drop-zone-image" src="@/assets/images/upload.svg" alt="upload" />
        Choose file
        <div class="subtitle">
          or drag-n-drop it here
        </div>
      </div>
    </div>
    <div v-else class="recorded-container">
      <div class="recorded-row">
        <img
          v-show="!isLoadingFile && !isDrawingWave"
          alt="Play / Pause"
          class="recorded-play"
          :src="uploadedFilePlayIcon"
          @click="playFile()"
        />
        <div class="recorded-waveform">
          <div id="uploadFileWaveForm" class="waveform"></div>
        </div>
        <div v-show="!isLoadingFile && !isDrawingWave">
          {{ uploadedFilePlaybackTime }}
        </div>
        <img
          v-show="!isLoadingFile && !isDrawingWave"
          src="@/assets/images/delete4.svg"
          class="recorded-delete"
          @click="deleteFile()"
          alt="delete"
        />
      </div>
      <span v-show="!isLoadingFile && !isDrawingWave" class="recorded-description">
        If you would like to upload a new file, please click the
        <img src="@/assets/images/delete4.svg" alt="delete" /> icon, and then select a new file.
      </span>
      <span v-show="isLoadingFile" class="recorded-description"> Loading file ({{ uploadedFilePercentage }}%)... </span>
      <span v-if="isDrawingWave" class="recorded-description">
        Building audio spectrogram...
      </span>
    </div>
  </div>
</template>

<script>
import { formatMMSS } from '@/utils';
import WaveSurfer from 'wavesurfer';

// list of the available audio file extensions
const extensionsList = ['mp3', 'wav'];

export default {
  name: 'PlayRecordingUploadFile',
  props: {
    setError: Function,
    uploadedFile: Blob,
    uploadedDuration: Number,
    initalLoading: Boolean
  },
  data() {
    return {
      isLoadingFile: true,
      isDrawingWave: false,
      uploaded: null,
      duration: 0,
      wave: null,
      uploadedFileIsPlaying: false,
      uploadedFileTimer: null,
      uploadedFileElapsedTime: 0,
      uploadedFilePercentage: 0
    };
  },
  mounted() {
    if (this.uploadFile) {
      this.uploaded = this.uploadedFile;
      this.duration = this.uploadedDuration;
      this.showTheWave();
    }
  },
  computed: {
    uploadedFilePlayIcon() {
      const name = this.uploadedFileIsPlaying ? 'stop' : 'play';
      const images = require.context('@/assets/images', false, /\.svg$/);
      return images('./' + name + '.svg');
    },
    uploadedFilePlaybackTime() {
      return formatMMSS(this.uploadedFileIsPlaying ? this.uploadedFileElapsedTime : this.duration);
    }
  },

  watch: {
    initalLoading() {
      if (this.uploadFile) {
        this.uploaded = this.uploadedFile;
        this.duration = this.uploadedDuration;

        this.showTheWave();
      }
    }
  },
  methods: {
    /**
     * Stop audio playback
     */
    stopPlayback() {
      if (this.wave) {
        this.wave.destroy();
      }
      clearInterval(this.uploadedFileTimer);
      this.uploadedFileIsPlaying = false;
      this.uploadedFileElapsedTime = 0;
    },
    /**
     * Check uploaded file
     * @param file {object} - File object
     * @returns {boolean}
     */
    checkUploadedFile(file = {}) {
      // check file
      if (!file) {
        return false;
      }

      // check file size (50MB max)
      if (file.size > 52428800) {
        return false;
      }

      // check file extension
      const [extension = ''] = file.name.split('.').reverse();
      return extension && extensionsList.includes(extension);
    },

    /**
     * Play / pause uploaded file
     */
    playFile() {
      if (!this.uploadedFileIsPlaying) {
        this.wave.play();
        this.uploadedFileTimer = setInterval(() => {
          this.uploadedFileElapsedTime += 1;
        }, 1000);
        this.uploadedFileIsPlaying = true;
      } else {
        this.wave.pause();
        clearInterval(this.uploadedFileTimer);
        this.uploadedFileIsPlaying = false;
      }
    },

    /**
     * Show WaveSurfer audio wave
     */
    showTheWave() {
      return this.$nextTick(async function() {
        try {
          // create File Reader
          const reader = new FileReader();

          this.isLoadingFile = true;
          this.isDrawingWave = false;

          reader.onload = (file) => {
            try {
              // convert file to Blob
              const blob = new Blob([new Uint8Array(file.target.result)]);

              // create a WaveSurfer instance for the "Upload file" tab
              this.wave = WaveSurfer.create({
                container: '#uploadFileWaveForm',
                waveColor: '#C2C7CF',
                progressColor: '#606C82',
                height: 70,
                barWidth: 1,
                barGap: 2,
                interact: false
              });

              // load Blob
              this.wave.loadBlob(blob);
              this.uploadedFilePercentage = 0;

              // track loading progress
              this.wave.on('loading', (percent = 0) => {
                this.uploadedFilePercentage = percent;
                if (Number(percent) === 100) {
                  this.isLoadingFile = false;
                  this.isDrawingWave = true;
                }
              });

              // track waveform drawing
              this.wave.on('ready', () => {
                this.isDrawingWave = false;
                this.duration = parseInt(this.wave.getDuration());
                this.$emit('store-audio', this.uploaded, this.duration);
                this.$emit('enable-save', true);
              });

              // handle end of playback event
              this.wave.on('finish', () => {
                clearInterval(this.uploadedFileTimer);

                this.uploadedFileElapsedTime = 0;
                this.uploadedFileIsPlaying = false;
              });
            } catch (error) {
              console.log('-- PLAY RECORDING -> UPLOAD FILE: ERROR\n', error);
              this.setError(error.message || error);
            }
          };

          reader.onerror = (error) => {
            console.log('-- PLAY RECORDING -> UPLOAD FILE: ERROR\n', error);
            this.setError(error.message || error);
          };

          // read file as an ArrayBuffer
          return reader.readAsArrayBuffer(this.uploaded);
        } catch (error) {
          console.log('-- PLAY RECORDING -> UPLOAD FILE: ERROR\n', error);
          this.setError(error.message || error);
        }
      });
    },

    /**
     * Drop file event for the "Upload File" tab
     * @param event {object} - event object
     */
    dropFile(event = {}) {
      const droppedFiles = event.dataTransfer.files;
      if (droppedFiles) {
        const check = this.checkUploadedFile([...droppedFiles].map((file) => file)[0]);
        if (check) {
          // clear the files array and store new file there
          this.uploaded = droppedFiles[0];
          this.showTheWave();
        }
      }
    },

    /**
     * Select file event for the "Upload file" tab
     * @param event {object} - event object
     */
    selectFile(event = {}) {
      const check = this.checkUploadedFile(event.target.files[0]);
      if (check) {
        // clear the files array and store new file there
        this.uploaded = event.target.files[0];
        this.showTheWave();
      }
    },

    /**
     * Delete recorded audio
     */
    async deleteFile() {
      // stop the playback first
      this.stopPlayback();

      // delete the file link
      this.uploaded = null;
      this.$emit('delete-audio');
      this.$emit('enable-save', false);
    }
  }
};
</script>

<style scoped lang="scss">
.container {
  width: 100%;
  padding: 0 40px;
  margin-top: 15px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-radius: 3px;
  .subtitle {
    color: #98a0ad;
    font-size: 14px;
    line-height: 20px;
    text-align: center;
    letter-spacing: -0.01em;
  }
}
.drop-zone {
  min-height: 125px;
  width: 100%;
  border: 2px solid transparent;
  border-radius: 5px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: #fafbfc;
  .aligned {
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }
  .drop-zone-image {
    width: 30px;
    height: 31px;
  }
}
.drop-zone:hover {
  border: 2px dotted #bcb5c1;
  cursor: pointer;
  transition: border 750ms;
}
.recorded-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  &.selected {
    position: relative;
    top: unset;
    left: unset;
  }
  .recorded-row {
    position: relative;
    height: 50px;
    display: flex;
    flex-direction: row;
    align-items: center;
    background-color: #f4f5f7;
    border-radius: 3px;
    padding: 0 20px;
    font-size: 14px;
    line-height: 20px;
    letter-spacing: -0.01em;
    color: #000000;
    .recorded-play {
      width: 13.33px;
      height: 16.67px;
      cursor: pointer;
    }
    .recorded-waveform {
      flex: 1;
      height: 50px;
      max-width: 380px;
      display: block;
      margin: 0 5px;
      overflow: hidden;
      .waveform {
        transform: translate(0, -10px);
      }
    }
    .recorded-delete {
      position: absolute;
      right: 12px;
      cursor: pointer;
    }
  }
  .recorded-description {
    font-size: 14px;
    line-height: 20px;
    letter-spacing: -0.01em;
    color: $colorPlaceholder;
    margin-top: 15px;
    width: 450px;
    .recorded-description-delete {
      width: 12.5px;
      height: 13.75px;
    }
  }
}
</style>
